import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
	AbstractCRUDService,
	AlarisApiService,
	ExtendableRefBookService
} from '@campaign-portal/components-library';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { ReadRequest, ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { TranslationRule } from '@helpers/types/models';
import {
	TranslationRulesCreateRequest,
	TranslationRulesCreateResponse, TranslationRulesDeleteRequest, TranslationRulesDeleteResponse,
	TranslationRulesReadRequest,
	TranslationRulesReadResponse, TranslationRulesUpdateRequest,
	TranslationRulesUpdateResponse
} from '@helpers/types/hlr';

@Injectable({
	providedIn: 'root'
})
export class TranslationRulesService extends ExtendableRefBookService<TranslationRule> implements AbstractCRUDService {


	readonly entity = '';
	readonly title = '';
	override readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService
	) {
		super();
	}

	override process(resp: ReadResponse<TranslationRule<number | null>[]>):ReadResponse<TranslationRule<number | null>[]> {
		this.list$.next(resp.Data);
		this.map$.next(this.list.reduce((result, item) => {
			result.set(item.id, item);
			return result;
		}, new Map()));
		return resp;
	}

	read(params: ReadRequest): Observable<ReadResponse<TranslationRule[]>> {
		const parsedParams: TranslationRulesReadRequest = this.prepareParams(params);
		return this.api.loader<TranslationRulesReadResponse>('TranslationRules.Read', parsedParams, this.loading$)
			.pipe(
				map((resp) => {
					return {
						Success: !!resp.data,
						Total: resp.length,
						Data: resp.data
					};
				}),
				map((resp) => {
					return this.process(resp);
				})
			);
	}

	update(params: TranslationRulesCreateRequest | TranslationRulesUpdateRequest):
		Observable<TranslationRulesUpdateResponse | TranslationRulesCreateResponse> {
		const method = params.id ? 'TranslationRules.Update' : 'TranslationRules.Create';
		return this.api.loader<TranslationRulesUpdateResponse | TranslationRulesCreateResponse>(method, params, this.loading$);
	}

	delete(params: TranslationRulesDeleteRequest): Observable<TranslationRulesDeleteResponse>{
		return this.api.loader<TranslationRulesDeleteResponse>('TranslationRules.Delete', params, this.loading$);
	}

	private prepareParams(params: RPCRequestParams): TranslationRulesReadRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? 25,
			active: 'id',
			direction: 'asc',
			filter: params.Filters
				? params.Filters.map((filter) => {
					return this.parseFilterField(filter);
				})
				: []
		};
	}

	private parseFilterField(filter: Filter): { key: string, value: string } {
		const variable = filter.Field;
		const value: any = filter.Value;
		switch (variable) {
			case 'status':
				return { key: 'status', value: (value === 'Active').toString() };
			default:
				return { key: variable, value: value + '' };
		}
	}
}
